// OptiFi platform console (ADR-0040): mahallus and their counts, new mahallus, suspension, demo requests,
// operators and the console activity log. It never shows member data; the API returns counts only.
import { useState, type ReactNode } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CONSOLE_CAN_CHANGE, LANGUAGES, LEAD_STATUSES, RESERVED_SLUGS, SLUG_PATTERN, type ConsoleTenant, type LeadStatus, type TenantStatus } from '@mahallu/shared';
import {
  ApiError,
  auth,
  consoleApi,
  loadErrorKey,
  useConsoleAudit,
  useConsoleLeads,
  useConsoleMe,
  useConsoleOperators,
  useConsoleOverview,
  useConsoleTenants,
  useMe,
} from '../api';
import { Wordmark } from '../components/Brand';
import { FormFooter, FormSection, Modal, SelectField, TextField } from '../components/Form';
import { Icon, type IconName } from '../components/Icon';
import { Badge, PageHeader, Stat } from '../components/Parts';
import { count, dateTime, initials } from '../format';
import { setLanguage } from '../i18n';
import { BASE_DOMAIN } from '../mahalluLink';

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError && e.status < 500 ? e.message : fallback);
const STATUS_TONE: Record<TenantStatus, 'ok' | 'warn' | 'bad'> = { LIVE: 'ok', TRIAL: 'warn', SUSPENDED: 'bad' };
const LEAD_TONE: Record<LeadStatus, 'info' | 'ok' | 'neutral'> = { NEW: 'info', CONTACTED: 'ok', CLOSED: 'neutral' };


/** Gate: a console session with the authenticator passed; anything else goes to the console sign-in. */
export function RequireConsole({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const location = useLocation();
  const me = useMe();
  if (me.isPending) return <div className="auth-wrap"><p className="note">{t('table.loading')}</p></div>;
  if (me.isError && !(me.error instanceof ApiError && me.error.status === 401)) {
    return <div className="auth-wrap"><p className="form-error" role="alert">{t('signin.error')}</p></div>;
  }
  if (!me.data || !me.data.console || me.data.mfa.state !== 'VERIFIED') return <Navigate to="/console/sign-in" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

const NAV: { to: string; key: string; icon: IconName }[] = [
  { to: '/console', key: 'console.nav.overview', icon: 'dash' },
  { to: '/console/mahallus', key: 'console.nav.mahallus', icon: 'building' },
  { to: '/console/requests', key: 'console.nav.requests', icon: 'mega' },
  { to: '/console/operators', key: 'console.nav.operators', icon: 'shield' },
  { to: '/console/activity', key: 'console.nav.activity', icon: 'ledger' },
];

export function ConsoleShell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const who = useConsoleMe(true);
  const signOut = async () => {
    try {
      await auth.logout();
    } finally {
      qc.clear();
      navigate('/console/sign-in', { replace: true });
    }
  };
  return (
    <div className="shell">
      <aside className="side" aria-label={t('console.title')}>
        <div className="side-brand">
          <Wordmark />
        </div>
        <div className="side-head">
          <div className="tenant-av" aria-hidden="true">O</div>
          <div>
            <b>{t('console.title')}</b>
            <span className="note">{t('console.scope')}</span>
          </div>
        </div>
        <nav className="side-list">
          <div className="acc-sec">{t('console.section')}</div>
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end className={({ isActive }) => `acc${isActive ? ' current' : ''}`}>
              <Icon name={n.icon} />
              <span>{t(n.key)}</span>
            </NavLink>
          ))}
        </nav>
        <div className="side-foot">{t('app.descriptor')}</div>
      </aside>
      <div className="main">
        <header className="hdr">
          <div className="crumb">{t('console.title')}</div>
          <div className="spacer" />
          <div className="lang" role="group" aria-label={t('header.language')}>
            {LANGUAGES.map((l) => (
              <button key={l.code} type="button" lang={l.code} className={l.code === 'ml' ? 'ml' : undefined} aria-pressed={i18n.language === l.code} onClick={() => setLanguage(l.code)}>
                {l.label}
              </button>
            ))}
          </div>
          {who.data ? (
            <div className="who-am-i" title={who.data.displayName}>
              <div className="avatar">{initials(who.data.displayName)}</div>
              <div className="who-text">
                <b>{who.data.displayName}</b>
                <span>{t(`console.role.${who.data.role}`)}</span>
              </div>
            </div>
          ) : null}
          <button type="button" className="btn sm" onClick={() => void signOut()}>
            <Icon name="logout" />
            {t('header.signOut')}
          </button>
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function useCanChange(): boolean {
  const who = useConsoleMe(true);
  return Boolean(who.data && CONSOLE_CAN_CHANGE.includes(who.data.role));
}

export function ConsoleOverviewPage() {
  const { t } = useTranslation();
  const o = useConsoleOverview();
  const d = o.data;
  return (
    <>
      <PageHeader icon="dash" title={t('console.overview.title')} desc={t('console.overview.desc')} />
      {o.isError ? <p className="form-error">{t(loadErrorKey(o.error))}</p> : null}
      {d ? (
        <>
          <div className="section-h">{t('console.overview.mahallus')}</div>
          <div className="kpis">
            <Stat icon="building" tone="brand" value={count(d.tenants.total)} label={t('console.overview.total')} />
            <Stat icon="check" tone="ok" value={count(d.tenants.live)} label={t('console.status.LIVE')} />
            <Stat icon="hash" tone="warn" value={count(d.tenants.trial)} label={t('console.status.TRIAL')} />
            <Stat icon="x" tone="bad" value={count(d.tenants.suspended)} label={t('console.status.SUSPENDED')} />
          </div>
          <div className="section-h">{t('console.overview.usage')}</div>
          <div className="kpis">
            <Stat icon="home" tone="teal" value={count(d.totals.houses)} label={t('console.col.houses')} />
            <Stat icon="users" tone="teal" value={count(d.totals.members)} label={t('console.col.members')} />
            <Stat icon="wallet" tone="violet" value={count(d.totals.receipts30d)} label={t('console.col.receipts30d')} />
            <Stat icon="file" tone="violet" value={count(d.totals.certificates)} label={t('console.col.certificates')} />
            <Stat icon="mega" tone="org" value={count(d.totals.messages30d)} label={t('console.col.messages30d')} />
            <Stat icon="bell" tone={d.totals.messagesFailed30d ? 'bad' : 'ok'} value={count(d.totals.messagesFailed30d)} label={t('console.col.failed30d')} />
          </div>
          <div className="section-h">{t('console.overview.desk')}</div>
          <div className="kpis">
            <Stat icon="mega" tone={d.leads.new ? 'warn' : 'ok'} value={count(d.leads.new)} label={t('console.overview.newRequests')} />
            <Stat icon="shield" tone="brand" value={count(d.operators)} label={t('console.nav.operators')} />
          </div>
          <p className="note">{t('console.noMemberData')}</p>
        </>
      ) : null}
    </>
  );
}

function NewMahallu({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [nameEn, setNameEn] = useState('');
  const [nameMl, setNameMl] = useState('');
  const [slug, setSlug] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminPhone, setAdminPhone] = useState('+91');
  const slugOk = SLUG_PATTERN.test(slug) && !(RESERVED_SLUGS as readonly string[]).includes(slug);
  const save = useMutation({
    mutationFn: () => consoleApi.createTenant({ nameEn: nameEn.trim(), nameMl: nameMl.trim() || null, slug, adminName: adminName.trim(), adminPhone: adminPhone.trim() }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['console'] });
      onClose();
    },
  });
  return (
    <Modal
      title={t('console.new.title')}
      onClose={onClose}
      footer={<FormFooter onCancel={onClose} saving={save.isPending} saveLabel={t('console.new.save')} error={save.isError ? errorText(save.error, t('signin.error')) : undefined} />}
    >
      <form
        id="active-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (slugOk) save.mutate();
        }}
      >
        <FormSection title={t('console.new.mahallu')}>
          <TextField label={t('console.new.nameEn')} value={nameEn} onChange={setNameEn} maxLength={120} required />
          <TextField label={t('console.new.nameMl')} value={nameMl} onChange={setNameMl} maxLength={120} lang="ml" />
          <TextField
            label={t('console.new.slug')}
            value={slug}
            onChange={(v) => setSlug(v.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            maxLength={63}
            required
            full
            hint={slug && !slugOk ? t('console.new.slugBad') : t('console.new.slugHint', { link: `${slug || 'yourmahallu'}.${BASE_DOMAIN}` })}
          />
        </FormSection>
        <FormSection title={t('console.new.admin')}>
          <TextField label={t('console.new.adminName')} value={adminName} onChange={setAdminName} maxLength={100} required />
          <TextField label={t('console.new.adminPhone')} value={adminPhone} onChange={setAdminPhone} maxLength={20} inputMode="tel" required hint={t('console.new.adminHint')} />
        </FormSection>
      </form>
    </Modal>
  );
}

function StatusChange({ tenant, onClose }: { tenant: ConsoleTenant; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [status, setStatus] = useState<TenantStatus | ''>(tenant.status === 'SUSPENDED' ? 'TRIAL' : 'SUSPENDED');
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () => consoleApi.setStatus(tenant.id, status as TenantStatus, reason.trim()),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['console'] });
      onClose();
    },
  });
  return (
    <Modal
      title={`${t('console.status.change')} · ${tenant.nameEn}`}
      onClose={onClose}
      footer={<FormFooter onCancel={onClose} saving={save.isPending} saveLabel={t('form.save')} error={save.isError ? errorText(save.error, t('signin.error')) : undefined} />}
    >
      <form
        id="active-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (status) save.mutate();
        }}
      >
        <FormSection title={t('console.status.section')}>
          <SelectField
            label={t('console.col.status')}
            value={status}
            onChange={setStatus}
            options={(['TRIAL', 'LIVE', 'SUSPENDED'] as const).map((s) => ({ value: s, label: t(`console.status.${s}`) }))}
            required
          />
          <TextField label={t('console.status.reason')} value={reason} onChange={setReason} maxLength={300} required full hint={t('console.status.reasonHint')} />
        </FormSection>
        {status === 'SUSPENDED' ? <p className="callout small">{t('console.status.suspendNote')}</p> : null}
      </form>
    </Modal>
  );
}

export function ConsoleMahallusPage() {
  const { t, i18n } = useTranslation();
  const tenants = useConsoleTenants();
  const canChange = useCanChange();
  const [adding, setAdding] = useState(false);
  const [changing, setChanging] = useState<ConsoleTenant | null>(null);
  return (
    <>
      <PageHeader
        icon="building"
        title={t('console.mahallus.title')}
        desc={t('console.mahallus.desc')}
        actions={canChange ? [{ label: t('console.new.title'), icon: 'plus', primary: true, onClick: () => setAdding(true) }] : []}
      />
      {tenants.isError ? <p className="form-error">{t(loadErrorKey(tenants.error))}</p> : null}
      <div className="tbl-wrap">
        <div className="tbl-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('console.col.mahallu')}</th>
                <th>{t('console.col.link')}</th>
                <th>{t('console.col.status')}</th>
                <th className="num">{t('console.col.houses')}</th>
                <th className="num">{t('console.col.members')}</th>
                <th className="num">{t('console.col.receipts30d')}</th>
                <th className="num">{t('console.col.certificates')}</th>
                <th className="num">{t('console.col.failed30d')}</th>
                <th>{t('console.col.lastActivity')}</th>
                {canChange ? <th>{t('col.actions')}</th> : null}
              </tr>
            </thead>
            <tbody>
              {tenants.data?.map((x) => (
                <tr key={x.id}>
                  <td>
                    <b>{x.nameEn}</b>
                    {x.nameMl ? <div className="ml note">{x.nameMl}</div> : null}
                    {x.isDemo ? <Badge tone="violet">{t('console.demo')}</Badge> : null}
                    {x.admins === 0 ? <Badge tone="warn">{t('console.noAdmin')}</Badge> : null}
                  </td>
                  <td className="mono">{`${x.slug}.${BASE_DOMAIN}`}</td>
                  <td>
                    <Badge tone={STATUS_TONE[x.status]}>{t(`console.status.${x.status}`)}</Badge>
                  </td>
                  <td className="num">{count(x.houses)}</td>
                  <td className="num">{count(x.members)}</td>
                  <td className="num">{count(x.receipts30d)}</td>
                  <td className="num">{count(x.certificates)}</td>
                  <td className="num">{count(x.messagesFailed30d)}</td>
                  <td>{x.lastActivity ? dateTime(x.lastActivity, i18n.language) : '—'}</td>
                  {canChange ? (
                    <td>
                      <button type="button" className="btn sm" onClick={() => setChanging(x)}>
                        {t('console.status.change')}
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
              {tenants.data && !tenants.data.length ? (
                <tr>
                  <td colSpan={10} className="tbl-msg">{t('console.mahallus.none')}</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
      <p className="note">{t('console.noMemberData')}</p>
      {adding ? <NewMahallu onClose={() => setAdding(false)} /> : null}
      {changing ? <StatusChange tenant={changing} onClose={() => setChanging(null)} /> : null}
    </>
  );
}

export function ConsoleRequestsPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const leads = useConsoleLeads();
  const setLead = useMutation({
    mutationFn: (v: { id: string; status: LeadStatus }) => consoleApi.setLead(v.id, v.status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['console'] }),
  });
  return (
    <>
      <PageHeader icon="mega" title={t('console.requests.title')} desc={t('console.requests.desc')} />
      {leads.isError ? <p className="form-error">{t(loadErrorKey(leads.error))}</p> : null}
      <div className="tbl-wrap">
        <div className="tbl-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('console.col.when')}</th>
                <th>{t('console.col.person')}</th>
                <th>{t('console.col.mahallu')}</th>
                <th>{t('console.col.contact')}</th>
                <th>{t('console.col.message')}</th>
                <th>{t('console.col.status')}</th>
              </tr>
            </thead>
            <tbody>
              {leads.data?.map((x) => (
                <tr key={x.id}>
                  <td>{dateTime(x.createdAt, i18n.language)}</td>
                  <td>{x.name}</td>
                  <td>
                    {x.mahalluName}
                    {x.place ? <div className="note">{x.place}</div> : null}
                  </td>
                  <td className="mono">{x.phone ?? x.email}</td>
                  <td>{x.message ?? '—'}</td>
                  <td>
                    <Badge tone={LEAD_TONE[x.status]}>{t(`console.lead.${x.status}`)}</Badge>{' '}
                    <select aria-label={t('console.col.status')} value={x.status} disabled={setLead.isPending} onChange={(e) => setLead.mutate({ id: x.id, status: e.target.value as LeadStatus })}>
                      {LEAD_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {t(`console.lead.${s}`)}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
              {leads.data && !leads.data.length ? (
                <tr>
                  <td colSpan={6} className="tbl-msg">{t('console.requests.none')}</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
      <p className="note">{t('console.requests.retention')}</p>
    </>
  );
}

export function ConsoleOperatorsPage() {
  const { t, i18n } = useTranslation();
  const ops = useConsoleOperators();
  return (
    <>
      <PageHeader icon="shield" title={t('console.operators.title')} desc={t('console.operators.desc')} />
      <p className="callout small">{t('console.operators.how')}</p>
      <div className="tbl-wrap">
        <div className="tbl-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('console.col.person')}</th>
                <th>{t('console.col.contact')}</th>
                <th>{t('console.col.role')}</th>
                <th>{t('console.col.status')}</th>
                <th>{t('console.col.since')}</th>
              </tr>
            </thead>
            <tbody>
              {ops.data?.map((x) => (
                <tr key={x.destination + x.createdAt}>
                  <td>{x.displayName}</td>
                  <td className="mono">{x.destination}</td>
                  <td>{t(`console.role.${x.role}`)}</td>
                  <td>
                    <Badge tone={x.status === 'ACTIVE' ? 'ok' : 'neutral'}>{t(`console.opStatus.${x.status}`)}</Badge>
                  </td>
                  <td>{dateTime(x.createdAt, i18n.language)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export function ConsoleActivityPage() {
  const { t, i18n } = useTranslation();
  const log = useConsoleAudit();
  return (
    <>
      <PageHeader icon="ledger" title={t('console.activity.title')} desc={t('console.activity.desc')} />
      <div className="tbl-wrap">
        <div className="tbl-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('console.col.when')}</th>
                <th>{t('console.col.person')}</th>
                <th>{t('console.col.action')}</th>
                <th>{t('console.col.mahallu')}</th>
                <th>{t('console.col.detail')}</th>
              </tr>
            </thead>
            <tbody>
              {log.data?.map((x) => (
                <tr key={x.id}>
                  <td>{dateTime(x.at, i18n.language)}</td>
                  <td>{x.who ?? t('console.activity.commandLine')}</td>
                  <td className="mono">{x.action}</td>
                  <td>{x.mahallu ?? '—'}</td>
                  <td className="mono small">{Object.entries(x.detail).filter(([k]) => k !== 'operator').map(([k, v]) => `${k}: ${String(v)}`).join(' · ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
